import { useState } from "react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import {
  useWhyChooseUsAdmin,
  useUpsertWhyChooseUs,
  useDeleteWhyChooseUs,
  type WhyChooseUsItem,
  type WhyChooseUsInput,
} from "@/hooks/admin/useWhyChooseUsAdmin";

const empty: WhyChooseUsInput = {
  title: "",
  description: "",
  icon: "CheckCircle",
  sort_order: 0,
  is_active: true,
};

export default function WhyChooseUsAdminPage() {
  const { data: list, isLoading } = useWhyChooseUsAdmin();
  const upsert = useUpsertWhyChooseUs();
  const remove = useDeleteWhyChooseUs();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<WhyChooseUsInput>(empty);
  const [open, setOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<WhyChooseUsItem | null>(null);

  function openNew() {
    setEditingId(null);
    setForm({ ...empty, sort_order: (list?.length ?? 0) * 10 + 10 });
    setOpen(true);
  }

  function openEdit(it: WhyChooseUsItem) {
    setEditingId(it.id);
    setForm({
      title: it.title,
      description: it.description,
      icon: it.icon ?? "CheckCircle",
      sort_order: it.sort_order,
      is_active: it.is_active,
    });
    setOpen(true);
  }

  function handleSave() {
    if (!form.title.trim() || !form.description.trim()) {
      toast.error("Title and description are required");
      return;
    }
    const payload = { ...form, icon: form.icon?.trim() || null };
    upsert.mutate(
      editingId ? { id: editingId, ...payload } : payload,
      {
        onSuccess: () => {
          toast.success(editingId ? "Updated" : "Added");
          setOpen(false);
        },
        onError: (e) => toast.error((e as Error).message || "Failed"),
      }
    );
  }

  function handleDelete() {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success("Deleted");
        setDeleteTarget(null);
      },
      onError: (e) => toast.error((e as Error).message || "Failed"),
    });
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl font-bold text-foreground">Why Choose Us</h1>
            <p className="text-muted-foreground mt-1">
              Benefits listed in the home "Why Clients Choose Us" section.
            </p>
          </div>
          <Button onClick={openNew} className="shrink-0">
            <Plus className="h-4 w-4 mr-2" /> New reason
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : list && list.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No reasons yet.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {list?.map((it) => (
              <Card key={it.id} className={!it.is_active ? "opacity-60" : ""}>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="h-5 w-5 shrink-0 text-primary mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-base font-semibold">{it.title}</h3>
                        <Badge variant="outline" className="text-[10px]">order {it.sort_order}</Badge>
                        {!it.is_active && <Badge variant="secondary">Hidden</Badge>}
                        {it.icon && <Badge variant="outline" className="text-[10px]">icon: {it.icon}</Badge>}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1.5">{it.description}</p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button variant="ghost" size="icon" aria-label="Edit reason" onClick={() => openEdit(it)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" aria-label="Delete reason" onClick={() => setDeleteTarget(it)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit reason" : "New reason"}</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label htmlFor="w-title">Title</Label>
                <Input
                  id="w-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Tailored to Your Event"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="w-desc">Description</Label>
                <Textarea
                  id="w-desc"
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Custom menus designed around your vision..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="grid gap-2">
                  <Label htmlFor="w-icon">Icon (lucide name)</Label>
                  <Input
                    id="w-icon"
                    value={form.icon ?? ""}
                    onChange={(e) => setForm({ ...form, icon: e.target.value })}
                    placeholder="CheckCircle"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="w-sort">Sort order</Label>
                  <Input
                    id="w-sort"
                    type="number"
                    value={form.sort_order}
                    onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <Label htmlFor="w-active">Visible on site</Label>
                <Switch
                  id="w-active"
                  checked={form.is_active}
                  onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={upsert.isPending}>
                {upsert.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save" : "Add"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this reason?</AlertDialogTitle>
              <AlertDialogDescription>
                "{deleteTarget?.title}" will be removed. Not recoverable.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                disabled={remove.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  );
}
